import React from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { House, Flag, ChartBar, Trophy, User, SignOut, ListBullets, Plus, UsersThree, Crown, Ticket, Lifebuoy } from "@phosphor-icons/react";
import NotificationsBell from "@/components/NotificationsBell";
import { BulaLogo } from "@/components/BulaLogo";

const NAV = [
  { to: "/dashboard", label: "Home", icon: House },
  { to: "/courses", label: "Courses", icon: Flag },
  { to: "/rounds", label: "Rounds", icon: ListBullets },
  { to: "/booking", label: "Book", icon: Ticket },
  { to: "/tournaments", label: "Events", icon: Trophy },
  { to: "/stats", label: "Stats", icon: ChartBar },
  { to: "/friends", label: "Friends", icon: UsersThree },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div className="min-h-screen bg-[#F8F7F3] text-[#0F2537] pb-24 md:pb-0">
      <header className="glass-header sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-3 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="logo-link">
            <BulaLogo size={36} />
            <div className="leading-none">
              <div className="font-heading font-black text-lg">Bula Golf</div>
              <div className="label-eyebrow !text-[0.55rem]">Fiji Handicap Tracker</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to} to={to} data-testid={`nav-${label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    isActive ? "bg-[#0F2537] text-white" : "text-[#0F2537]/80 hover:bg-black/5"
                  }`
                }
              >
                <Icon size={18} weight={location.pathname.startsWith(to) ? "fill" : "regular"} />
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/rounds/new")}
              data-testid="new-round-header-btn"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#E05D3A] text-white text-sm font-semibold hover:brightness-110 transition"
            >
              <Plus size={16} weight="bold" /> New Round
            </button>
            <NotificationsBell />
            <Link to="/help" data-testid="help-link" title="How-to guide" className="hidden sm:inline-flex w-9 h-9 rounded-full items-center justify-center text-[#0F2537]/70 hover:bg-black/5 hover:text-[#0F2537] transition">
              <Lifebuoy size={18} weight="regular" />
            </Link>
            {!user?.is_pro && (
              <Link to="/pricing" data-testid="go-pro-cta" className="hidden md:inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#0F2537] text-white text-xs font-bold hover:bg-[#0F2537]/90">
                <Crown size={12} weight="fill" className="text-[#E05D3A]" /> Go Pro
              </Link>
            )}
            {user?.is_pro && (
              <span data-testid="pro-badge" className="hidden md:inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#E05D3A]/10 text-[#E05D3A] text-xs font-bold">
                <Crown size={12} weight="fill" /> PRO
              </span>
            )}
            <Link to="/profile" data-testid="profile-link" className="w-9 h-9 rounded-full bg-[#0F2537] text-white flex items-center justify-center font-bold text-sm">
              {(user?.name || "?").slice(0, 1).toUpperCase()}
            </Link>
            <button onClick={logout} data-testid="logout-btn" className="text-[#0F2537]/70 hover:text-[#0F2537] p-2" title="Sign out">
              <SignOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-6 md:py-10">{children}</main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-xl border-t border-[#E2E0D8]">
        <div className="grid grid-cols-7">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to} to={to} data-testid={`mobile-nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center py-2.5 text-[10px] font-semibold ${
                  isActive ? "text-[#E05D3A]" : "text-[#0F2537]/70"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon size={20} weight={isActive ? "fill" : "regular"} />
                  <span className="mt-0.5">{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
